import React, { useState, useEffect, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import PropTypes from 'prop-types';
import Producto from './Producto';
import ScrollToTop from '../ScrollTop';
import { cargarProductosSubcategoria, buscarTituloPorCategoria } from '../../utils/loaders';

export default function Listado({ selectedFilters, sort }) {
  const [inicio, setInicio] = useState(0);
  const [fin, setFin] = useState(19);
  const [productos, setProductos] = useState([]);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shouldReload, setShouldReload] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizeData = (data) =>
    data.map((item) => ({
      id: item.id || item.producto_id,
      fecha_de_adicion: item.fecha_de_adicion || item.producto_fecha_de_adicion,
      visitas: item.visitas || item.producto_visitas,
      puntuacion: item.puntuacion || item.producto_puntuacion,
      numero_de_revisiones: item.numero_de_revisiones || item.producto_numero_de_revisiones,
      titulo: item.titulo || item.producto_titulo,
      estado: item.estado || item.producto_estado,
      descripcion: item.descripcion || item.producto_descripcion,
      imagenUrl: item.imagen_url || item.producto_imagen_url,
      subcategoria_id: item.subcategoria_id || item.categoria_id,
      pdfUrl: item.pdfUrl || item.producto_pdf_url,
    }));

  const cargarMasProductos = useCallback(async () => {
    setIsLoading(true);

    let subcategoriaIds = selectedFilters.flatMap((filter) =>
      filter.values.map((value) => value.categoria_id)
    );

    if (subcategoriaIds.length === 0) {
      subcategoriaIds = [5, 6, 7, 8, 9, 10]; // Default: categorías Hytera
    }

    const { data, error: errorAPI } = await cargarProductosSubcategoria(
      inicio,
      fin,
      subcategoriaIds
    );

    if (errorAPI) {
      setError(errorAPI.message || 'Error cargando productos');
      setHasMoreItems(false);
      setIsLoading(false);
      return;
    }

    if (data?.length > 0) {
      const normalizedData = normalizeData(data);
      setProductos((prev) => [...prev, ...normalizedData]);
      setInicio((prev) => prev + normalizedData.length);
      setFin((prev) => prev + normalizedData.length);
    } else {
      setHasMoreItems(false);
    }

    setIsLoading(false);
  }, [inicio, fin, selectedFilters]);

  const reiniciarEstadosYCargarProductos = () => {
    setProductos([]);
    setInicio(0);
    setFin(19);
    setHasMoreItems(true);
    setShouldReload(true);
  };

  useEffect(() => {
    if (shouldReload) {
      cargarMasProductos();
      setShouldReload(false);
    }
  }, [shouldReload, cargarMasProductos]);

  useEffect(() => {
    reiniciarEstadosYCargarProductos();
  }, [selectedFilters, sort]);

  const renderContenido = () => {
    if (error) {
      return (
        <div className="flex items-center p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50">
          <svg
            className="flex-shrink-0 w-4 h-4 mr-3"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.5 9.5 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z" />
          </svg>
          <span className="font-medium">ERROR:</span> {error}
        </div>
      );
    }

    if (!isLoading && productos.length === 0 && searchTerm.trim() !== '') {
      return (
        <div className="w-full text-center py-10 text-gray-500">
          <p className="text-lg font-semibold">😕 No se encontraron productos con ese término.</p>
          <p className="text-sm">Intenta con otro nombre o revisa la categoría seleccionada.</p>
        </div>
      );
    }

    return (
      <InfiniteScroll
        dataLength={productos.length}
        next={cargarMasProductos}
        hasMore={hasMoreItems}
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 md:grid-cols-4 md:gap-y-0 lg:gap-x-8">
          {productos.map((producto) => (
            <Producto
              key={producto.id}
              titulo={producto.titulo}
              estado={producto.estado}
              descripcion={producto.descripcion}
              imagenUrl={producto.imagenUrl}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      </InfiniteScroll>
    );
  };

  return (
    <div className="bg-white">
      <ScrollToTop />
      <div className="my-6">
        <input
          type="text"
          placeholder="Buscar dentro de esta categoría..."
          value={searchTerm}
          onChange={async (e) => {
            const term = e.target.value;
            setSearchTerm(term);

            if (term.length < 1) {
              reiniciarEstadosYCargarProductos(); // Limpia y recarga normalmente
              return;
            }

            let subcategoriaIds = selectedFilters.flatMap((filter) =>
              filter.values.map((value) => value.categoria_id)
            );

            if (subcategoriaIds.length === 0) {
              subcategoriaIds = [5, 6, 7, 8, 9, 10];
            }

            setIsLoading(true);
            const { data, error: errorBusqueda } = await buscarTituloPorCategoria(term, subcategoriaIds);
            if (errorBusqueda) {
              setError(errorBusqueda.message || 'Error en la búsqueda');
              setIsLoading(false);
              return;
            }

            const normalized = normalizeData(data);
            setProductos(normalized);
            setHasMoreItems(false); // No usar infinite scroll durante búsqueda
            setIsLoading(false);
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
        />

      </div>
      <div>
        {renderContenido()}
      </div>
    </div>
  );
}

Listado.propTypes = {
  sort: PropTypes.string.isRequired,
  selectedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          categoria_id: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
};

